import React, { createContext, useContext, useEffect, useState } from 'react';
import getCookie from '../components/content/MyAccount/Services/Cookies';
import { verifyToken } from '../components/content/MyAccount/Services/NowIdTokenService';
import { buildCustInfo, getProfile } from '../Services/NowIdProfileService';
import {
  ENV,
  API_URL,
  SOURCE_URL,
  GetStarted_API_TOKEN,
  ID_URL,
  TV_BINDING_URL,
} from '../config';
import language from '../utils/LanguageSettings';
import { normalizeSiteLang, resolveUserDisplayName } from '../utils/userIdentity';

interface AuthState {
  /** true while the initial auth check is in progress */
  loading: boolean;
  /** true once auth completed (regardless of logged-in or not) */
  ready: boolean;
  /** true when the user has a valid session */
  isLoggedIn: boolean;
  /** normalized user payload that components can consume without WP globals */
  userData: AuthUserData | null;
  /** true only after the async verifyToken/getProfile flow finishes (success or failure) */
  authVerified: boolean;
}

export interface AuthUserData {
  currentLang: string;
  nowSessionId: string;
  npid: string;
  nowid: string;
  username: string;
  nickname: string;
  email: string;
  mobile: string;
  fsa: string;
  fsaType: string;
  cust_type: string;
  readable_cust_type: number | string;
  accountStatus: string;
  novaXIndicator: boolean;
  verifiedDate: string;
  mobileVerifiedDate: string;
  is_user_ott: boolean;
  login_url: string;
  logout_url: string;
  site_url: string;
  image_not_found: string;
  tv_binding: string;
  writtenLang: string;
  react_app_env: string;
  get_started_token: string;
  strapi_api_domain: string;
  strapi_source_url: string;
  menus: {
    desktopPrimary: any[];
    mobilePrimary: any[];
    mobileSecondary: any[];
    watchDesktop: any[];
    watchMobile: any[];
    logo: string;
  };
  socialLinks: {
    facebook: string;
    instagram: string;
    youtube: string;
  };
  hideHeader: boolean;
  enableLoginOverlay: boolean;
  alertContent?: Record<string, any>;
  afterLoginModal?: Record<string, any>;
}

interface AuthProviderProps {
  children: React.ReactNode;
  bootstrapData?: Record<string, unknown>;
}

const buildAuthUserData = (
  source: Record<string, any>,
  currentLangFallback?: string
): AuthUserData => {
  const currentLang = normalizeSiteLang(
    (source.currentLang || currentLangFallback || language || 'en').toString()
  );

  return {
    ...source,
    currentLang,
    nowSessionId: (source.nowSessionId || '').toString(),
    npid: (source.npid || '').toString(),
    nowid: (source.nowid || '').toString(),
    username: resolveUserDisplayName(
      {
        username: source.username,
        nickname: source.nickname,
        email: source.email,
        mobile: source.mobile,
        nowid: source.nowid
      }
    ),
    nickname: (source.nickname || '').toString(),
    email: (source.email || '').toString(),
    mobile: (source.mobile || '').toString(),
    fsa: (source.fsa || '').toString(),
    fsaType: (source.fsaType || '').toString(),
    cust_type: (source.cust_type || 'not-logged-in').toString(),
    readable_cust_type: source.readable_cust_type || '',
    accountStatus: (source.accountStatus || '').toString(),
    novaXIndicator: Boolean(source.novaXIndicator),
    verifiedDate: (source.verifiedDate || '').toString(),
    mobileVerifiedDate: (source.mobileVerifiedDate || '').toString(),
    is_user_ott: Boolean(source.is_user_ott),
    login_url: (source.login_url || '').toString(),
    logout_url: (source.logout_url || `${ID_URL || ''}nowidform/logout.do?redirect=${typeof window !== 'undefined' ? window.location.origin : ''}`).toString(),
    site_url: (source.site_url || SOURCE_URL || (typeof window !== 'undefined' ? window.location.origin : '')).toString(),
    image_not_found: (source.image_not_found || (currentLang === 'en'
      ? 'https://nowtv-strapi-bucket.s3.ap-southeast-1.amazonaws.com/notfound_wbg_e_b36b9bbafa.png'
      : 'https://nowtv-strapi-bucket.s3.ap-southeast-1.amazonaws.com/notfound_wbg_c_1db0d3a970.png')).toString(),
    tv_binding: (source.tv_binding || TV_BINDING_URL || '').toString(),
    writtenLang: (source.writtenLang || (currentLang === 'zh' ? 'zh_Hant' : 'en')).toString(),
    react_app_env: (source.react_app_env || ENV || '').toString(),
    get_started_token: (source.get_started_token || GetStarted_API_TOKEN || '').toString(),
    strapi_api_domain: (source.strapi_api_domain || API_URL || '').toString(),
    strapi_source_url: (source.strapi_source_url || SOURCE_URL || '').toString(),
    menus: {
      desktopPrimary: source.menus?.desktopPrimary || [],
      mobilePrimary: source.menus?.mobilePrimary || [],
      mobileSecondary: source.menus?.mobileSecondary || [],
      watchDesktop: source.menus?.watchDesktop || [],
      watchMobile: source.menus?.watchMobile || [],
      logo: source.menus?.logo || '',
    },
    socialLinks: {
      facebook: source.socialLinks?.facebook || 'https://www.facebook.com/NowTV.hongkong/',
      instagram: source.socialLinks?.instagram || 'https://www.instagram.com/nowtv.hk',
      youtube: source.socialLinks?.youtube || 'https://www.youtube.com/user/nowtv',
    },
    hideHeader: Boolean(source.hideHeader),
    enableLoginOverlay: Boolean(source.enableLoginOverlay),
    alertContent: source.alertContent || {},
    afterLoginModal: source.afterLoginModal || {},
  };
};

const buildInitialAuthState = (bootstrapData?: Record<string, unknown>): AuthState => {
  const bootstrap = (bootstrapData || {}) as Record<string, any>;
  const hydratedUserData = buildAuthUserData(bootstrap);
  const bootstrapSessionId = typeof bootstrap.nowSessionId === 'string'
    ? bootstrap.nowSessionId
    : '';
  const bootstrapCustType = (bootstrap.cust_type || '').toString().toLowerCase();
  const bootstrapLoggedIn = Boolean(
    bootstrapSessionId || (bootstrapCustType && bootstrapCustType !== 'not-logged-in')
  );

  return {
    // If bootstrap already contains session hints, render immediately and
    // validate in background to avoid a visible post-login loading flash.
    loading: !bootstrapLoggedIn,
    ready: bootstrapLoggedIn,
    isLoggedIn: bootstrapLoggedIn,
    userData: hydratedUserData,
    authVerified: false,
  };
};

const AuthContext = createContext<AuthState>({
  loading: true,
  ready: false,
  isLoggedIn: false,
  userData: null,
  authVerified: false,
});

export const useAuth = () => useContext(AuthContext);

/**
 * Root-level auth provider that follows the exact same logic as MyAccountWrapper:
 *   verifyToken(undefined) → getProfile → buildCustInfo
 *
 * Key difference from MyAccountWrapper:
 *   - Does NOT redirect to login on failure (public pages still render)
 *   - Always renders children (shows loading indicator briefly, then content)
 *   - Exposes normalized auth state through React context (`useAuth`).
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children, bootstrapData = {} }) => {
  const [authState, setAuthState] = useState<AuthState>(() => buildInitialAuthState(bootstrapData));

  useEffect(() => {
    // Immediately hydrate from bootstrap payload so header/profile can render without waiting
    // for follow-up verifyToken/getProfile API calls.
    setAuthState(buildInitialAuthState(bootstrapData));

    const initialize = async () => {
      const currentLang = language;
      const bootstrap = bootstrapData as Record<string, any>;
      const bootstrapSessionId = typeof bootstrap.nowSessionId === 'string'
        ? bootstrap.nowSessionId
        : '';
      const bootstrapCustType = (bootstrap.cust_type || '').toString().toLowerCase();
      const bootstrapLoggedIn = Boolean(
        bootstrapSessionId || (bootstrapCustType && bootstrapCustType !== 'not-logged-in')
      );

      const buildBridgePayload = (overrides: Partial<AuthUserData> = {}): AuthUserData => {
        const merged = {
          ...bootstrap,
          ...overrides,
        } as Record<string, any>;

        return buildAuthUserData(merged, currentLang);
      };

      try {
        // Call verifyToken with undefined — the PHP proxy reads the
        // HttpOnly NOWSESSIONID cookie server-side and fills in the token.
        const localToken = getCookie();
        const tokenForVerification = localToken || bootstrapSessionId || undefined;
        const tokenData = await verifyToken(tokenForVerification);
        const sessionFromProxy =
          (tokenData && (tokenData.sessionId || tokenData.nowSessionId)) || '';
        const nowSessionId = sessionFromProxy || localToken || bootstrapSessionId || '';

        const tokenSuccess =
          (tokenData?.resultMessage || '').toString().toLowerCase() === 'success';
        const npid = tokenData?.data?.npid || tokenData?.npid || '';

        if (!nowSessionId || !tokenSuccess || !npid) {
          // No valid session.
          const anonymousData = buildBridgePayload({ nowSessionId: '' });
          setAuthState({ loading: false, ready: true, isLoggedIn: false, userData: anonymousData, authVerified: true });
          return;
        }

        // Cache the session so client-side reads (Header, etc.) find it
        if (typeof window !== 'undefined') {
          (window as Window & { __NOWSESSIONID?: string }).__NOWSESSIONID = nowSessionId;
        }

        const profileData = await getProfile(npid);
        const profileSuccess =
          (profileData?.resultMessage || '').toString().toLowerCase() === 'success';
        const profileAccountStatus = (profileData?.data?.accountStatus || '').toString().toUpperCase();

        if (!profileSuccess || profileAccountStatus !== 'ACTIVE') {
          const inactiveData = buildBridgePayload({ nowSessionId: '' });
          setAuthState({ loading: false, ready: true, isLoggedIn: false, userData: inactiveData, authVerified: true });
          return;
        }

        const custInfo: any = await buildCustInfo(nowSessionId, tokenData, profileData);

        const resolvedFsa = custInfo.fsa || '';
        const resolvedFsaType = custInfo.fsaType || '';
        const resolvedNowId = custInfo.nowid || '';
        const resolvedEmail = custInfo.email || '';
        const resolvedMobile = custInfo.mobile || '';
        const resolvedNickname = custInfo.nickname || '';

        const isUserOtt =
          resolvedFsaType === 'OTT' ||
          resolvedFsa === 'OTT' ||
          (!resolvedFsa && /^[0-9]+$/.test(resolvedNowId || ''));

        const resolvedUsername = resolveUserDisplayName(
          {
            username: typeof bootstrap.username === 'string' ? bootstrap.username : null,
            nickname: typeof bootstrap.nickname === 'string' ? bootstrap.nickname : null,
            email: resolvedEmail,
            mobile: resolvedMobile,
            nowid: resolvedNowId
          }
        );

        const siteUrl = SOURCE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
        const normalizedUserData: AuthUserData = buildBridgePayload({
          currentLang,
          nowSessionId,
          npid: custInfo.npid || '',
          nowid: resolvedNowId,
          username: resolvedUsername,
          nickname: resolvedNickname,
          email: resolvedEmail,
          mobile: resolvedMobile,
          fsa: resolvedFsa,
          fsaType: resolvedFsaType,
          cust_type: custInfo.cust_type || 'not-logged-in',
          readable_cust_type: custInfo.readable_cust_type || '',
          accountStatus: custInfo.accountStatus || '',
          novaXIndicator: Boolean(custInfo.novaXIndicator),
          verifiedDate: custInfo.verifiedDate || '',
          mobileVerifiedDate: custInfo.mobileVerifiedDate || '',
          is_user_ott: isUserOtt,
          login_url: `${siteUrl}/my-account`,
          logout_url: `${ID_URL || ''}nowidform/logout.do?redirect=${typeof window !== 'undefined' ? window.location.origin : ''}`,
          site_url: siteUrl,
          image_not_found: currentLang === 'en'
            ? 'https://nowtv-strapi-bucket.s3.ap-southeast-1.amazonaws.com/notfound_wbg_e_b36b9bbafa.png'
            : 'https://nowtv-strapi-bucket.s3.ap-southeast-1.amazonaws.com/notfound_wbg_c_1db0d3a970.png',
          tv_binding: TV_BINDING_URL || '',
          writtenLang: currentLang === 'zh' ? 'zh_Hant' : 'en',
          react_app_env: ENV || '',
          get_started_token: GetStarted_API_TOKEN || '',
          strapi_api_domain: API_URL || '',
          strapi_source_url: SOURCE_URL || '',
        });

        console.log('✅ Root auth completed', {
          cust_type: custInfo.cust_type,
          npid: custInfo.npid,
        });

        setAuthState({
          loading: false,
          ready: true,
          isLoggedIn: true,
          userData: normalizedUserData,
          authVerified: true,
        });
      } catch (err: any) {
        // Safe middle-ground:
        // If it's a temporary network error (no response status) or server error (5xx),
        // we can keep the user logged in using the bootstrapped session.
        // But if the server explicitly returned an Auth/Client failure (400, 401, 403),
        // we know the session is invalid, so we log them out.
        const status = err?.response?.status;
        const isTemporaryError = !status || status >= 500;

        if (bootstrapLoggedIn && isTemporaryError) {
          console.warn('⚠️ Auth verification encountered a network/server error; keeping bootstrapped session:', err);
          const bridgedData = buildBridgePayload({ nowSessionId: bootstrapSessionId });
          setAuthState({ loading: false, ready: true, isLoggedIn: true, userData: bridgedData, authVerified: true });
          return;
        }

        // Auth failed — not logged in, but still render the page
        console.log('ℹ️ Root auth: no active session', err);
        const fallbackData = buildBridgePayload({ nowSessionId: '' });
        setAuthState({ loading: false, ready: true, isLoggedIn: false, userData: fallbackData, authVerified: true });
      }
    };

    initialize();
  }, [bootstrapData]);

  return (
    <AuthContext.Provider value={authState}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
